import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, GraduationCap, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Find Courses", to: "/courses" },
  { label: "Universities", to: "/universities" },
  { label: "Countries", to: "/countries" },
  { label: "Course Categories", to: "/categories" },
  { label: "Listing Plans", to: "/pricing" },
  { label: "For Universities", to: "/for-universities" },
];

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    api.get("/public/menu-pages").then((r) => setPages(r.data.main || [])).catch(() => {});
  }, []);

  const navItems = [...NAV, ...pages.map((p) => ({ label: p.title, to: `/page/${p.slug}` }))];

  const dashPath = user && user.role === "admin" ? "/admin" : "/dashboard";

  const Logo = (
    <Link to="/" className="flex items-center" data-testid="site-logo">
      <img src="/oed-logo.gif" alt="Online Education Directory" className="h-7 w-auto md:h-9" />
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
        {Logo}
        <nav className="hidden items-center gap-6 lg:flex">
          {navItems.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-800"
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-2 md:flex">
          {user && user !== false ? (
            <>
              <Button asChild variant="outline" size="sm" data-testid="header-dashboard-btn">
                <Link to={dashPath}><LayoutDashboard className="mr-1.5 h-4 w-4" />Dashboard</Link>
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { logout(); navigate("/"); }} data-testid="header-logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="header-login-btn">
                <Link to="/login">University Login</Link>
              </Button>
              <Button asChild size="sm" className="bg-blue-800 hover:bg-blue-900" data-testid="header-list-btn">
                <Link to="/register">List Your Courses</Link>
              </Button>
            </>
          )}
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72">
            <div className="mb-6 mt-2">{Logo}</div>
            <nav className="flex flex-col gap-1">
              {navItems.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="rounded-md px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-100"
                >
                  {n.label}
                </Link>
              ))}
              <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-4">
                {user && user !== false ? (
                  <>
                    <Button asChild variant="outline" onClick={() => setOpen(false)}>
                      <Link to={dashPath}>Dashboard</Link>
                    </Button>
                    <Button variant="ghost" onClick={() => { logout(); setOpen(false); navigate("/"); }}>Sign out</Button>
                  </>
                ) : (
                  <>
                    <Button asChild variant="outline" onClick={() => setOpen(false)}>
                      <Link to="/login">University Login</Link>
                    </Button>
                    <Button asChild className="bg-blue-800 hover:bg-blue-900" onClick={() => setOpen(false)}>
                      <Link to="/register">List Your Courses</Link>
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
